// Frontend/src/pages/resident/screens/AnnouncementsPage.tsx
//
// What the building has told you, read-only.
//
// Rebuilt on the shared design system in M2. It had its own four-colour
// priority scale — MEDIUM blue, LOW green — where issues and payments use
// amber and slate, so the same word meant two different things depending on
// which tab you were on.
//
// Pinned announcements get their own section rather than a small amber icon
// on an otherwise identical card. Pinning is the admin saying "this one still
// matters after it stopped being new", and a 4-pixel difference does not
// carry that.

import React from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarDays, Megaphone, Pin } from "lucide-react";

import { useActiveAnnouncements, useAnnouncement } from "@/api/announcements";
import ResponsiveModal from "@/components/common/ResponsiveModal";
import SegmentedTabs from "@/components/common/SegmentedTabs";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  ANNOUNCEMENT_PRIORITY_ICON,
  ANNOUNCEMENT_PRIORITY_TONE,
  announcementPriorityIsLoud,
} from "@/lib/announcementStyles";
import type { AnnouncementListItem, AnnouncementPriority } from "@/types/announcement";

type Filter = "ALL" | AnnouncementPriority;

export default function AnnouncementsPage() {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = React.useState<Filter>("ALL");

  const { data: announcements, isLoading } = useActiveAnnouncements();

  // The bell links here as /resident/announcements?announcement=<id>. Opening
  // the notification has to open the announcement, or the bell sends people
  // to a list and leaves them to find it.
  const selectedId = searchParams.get("announcement");

  function open(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set("announcement", id);
    setSearchParams(next, { replace: true });
  }

  function close() {
    const next = new URLSearchParams(searchParams);
    next.delete("announcement");
    setSearchParams(next, { replace: true });
  }

  const dateLocale = language === "es" ? "es-ES" : "en-US";
  const formatDate = React.useCallback(
    (value: string) => new Intl.DateTimeFormat(dateLocale).format(new Date(value)),
    [dateLocale]
  );

  const { pinned, rest } = React.useMemo(() => {
    const list = (announcements ?? []).filter(
      (a) => filter === "ALL" || a.priority === filter
    );
    const byDate = (a: AnnouncementListItem, b: AnnouncementListItem) =>
      +new Date(b.published_at) - +new Date(a.published_at);

    return {
      pinned: list.filter((a) => a.is_pinned).sort(byDate),
      rest: list.filter((a) => !a.is_pinned).sort(byDate),
    };
  }, [announcements, filter]);

  const total = pinned.length + rest.length;

  return (
    <div className="relative space-y-4">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

      <div className="rounded-2xl bg-white/80 p-4 ring-1 ring-black/5 backdrop-blur">
        <h2 className="text-[17px] font-semibold text-gray-900">
          {t("announcements.activeTitle")}
        </h2>
        <p className="mt-0.5 text-[13px] text-gray-600">
          {isLoading ? t("common.loading") : t("announcements.count", { count: total })}
        </p>
      </div>

      <SegmentedTabs<Filter>
        ariaLabel={t("announcements.filterPriority")}
        value={filter}
        onChange={setFilter}
        items={[
          { key: "ALL", label: t("announcements.filterAll") },
          { key: "urgent", label: t("common.priority.urgent") },
          { key: "high", label: t("common.priority.high") },
          { key: "medium", label: t("common.priority.medium") },
          { key: "low", label: t("common.priority.low") },
        ]}
      />

      {isLoading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-2xl bg-white/70 ring-1 ring-black/5"
            />
          ))}
        </div>
      )}

      {!isLoading && total === 0 && (
        <div className="rounded-2xl bg-white/80 py-14 text-center ring-1 ring-black/5">
          <Megaphone className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">
            {filter === "ALL" ? t("announcements.empty") : t("announcements.noneMatch")}
          </p>
        </div>
      )}

      {pinned.length > 0 && (
        <section className="space-y-2">
          <h3 className="flex items-center gap-1.5 px-1 text-[11px] font-bold uppercase tracking-[0.08em] text-amber-700">
            <Pin className="h-3.5 w-3.5" />
            {t("announcements.pinnedSection")}
          </h3>
          <div className="space-y-3">
            {pinned.map((a) => (
              <AnnouncementCard
                key={a.id}
                item={a}
                pinned
                formatDate={formatDate}
                onOpen={() => open(a.id)}
              />
            ))}
          </div>
        </section>
      )}

      {rest.length > 0 && (
        <section className="space-y-2">
          {pinned.length > 0 && (
            <h3 className="px-1 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
              {t("announcements.recentSection")}
            </h3>
          )}
          <div className="space-y-3">
            {rest.map((a) => (
              <AnnouncementCard
                key={a.id}
                item={a}
                formatDate={formatDate}
                onOpen={() => open(a.id)}
              />
            ))}
          </div>
        </section>
      )}

      {selectedId && (
        <DetailModal announcementId={selectedId} formatDate={formatDate} onClose={close} />
      )}
    </div>
  );
}

/* ================================================================== */

function AnnouncementCard({
  item,
  pinned,
  formatDate,
  onOpen,
}: {
  item: AnnouncementListItem;
  pinned?: boolean;
  formatDate: (value: string) => string;
  onOpen: () => void;
}) {
  const { t } = useLanguage();
  const tone = ANNOUNCEMENT_PRIORITY_TONE[item.priority];
  const Icon = ANNOUNCEMENT_PRIORITY_ICON[item.priority];

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`relative block w-full overflow-hidden rounded-2xl bg-white p-4 text-left transition hover:bg-gray-50 ${
        pinned ? "ring-1 ring-amber-300" : "ring-1 ring-black/5"
      }`}
    >
      <span className="absolute inset-y-0 left-0 w-1" style={{ background: tone.rail }} />

      <div className="flex items-start gap-3 pl-2">
        <span
          className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl"
          style={{ background: tone.bg, color: tone.fg }}
        >
          <Icon className="h-4 w-4" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <h3 className="min-w-0 flex-1 text-[15px] font-semibold leading-snug text-gray-900">
              {item.title}
            </h3>
            {pinned && <Pin className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-gray-500">
            <span
              className="rounded-full px-2 py-0.5 text-[11px] font-medium"
              style={{
                background: announcementPriorityIsLoud(item.priority) ? tone.rail : tone.bg,
                color: announcementPriorityIsLoud(item.priority) ? "#fff" : tone.fg,
              }}
            >
              {t(`common.priority.${item.priority}`)}
            </span>
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-3.5 w-3.5" />
              {formatDate(item.published_at)}
            </span>
            <span>{t("announcements.postedBy", { name: item.author_name })}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

/* ================================================================== */

function DetailModal({
  announcementId,
  formatDate,
  onClose,
}: {
  announcementId: string;
  formatDate: (value: string) => string;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const { data: a, isLoading, isError } = useAnnouncement(announcementId);

  if (isLoading || !a) {
    return (
      <ResponsiveModal open onClose={onClose} title={t("announcements.activeTitle")}>
        <p className="py-10 text-center text-sm text-gray-500">
          {/* An expired announcement 404s here: the resident queryset hides
              it the moment it expires, and the bell link outlives it. */}
          {isError ? t("announcements.goneHint") : t("common.loading")}
        </p>
      </ResponsiveModal>
    );
  }

  const tone = ANNOUNCEMENT_PRIORITY_TONE[a.priority];

  return (
    <ResponsiveModal
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          {a.is_pinned && <Pin className="h-4 w-4 shrink-0 text-amber-600" />}
          {a.title}
        </span>
      }
      subtitle={t("announcements.postedBy", { name: a.author?.full_name || t("common.unknown") })}
      headerAccent={tone.rail}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
            style={{
              background: announcementPriorityIsLoud(a.priority) ? tone.rail : tone.bg,
              color: announcementPriorityIsLoud(a.priority) ? "#fff" : tone.fg,
            }}
          >
            {t(`common.priority.${a.priority}`)}
          </span>
          <span className="inline-flex items-center gap-1 text-[12px] text-gray-500">
            <CalendarDays className="h-3.5 w-3.5" />
            {formatDate(a.published_at)}
          </span>
          {a.expires_at && (
            <span className="text-[12px] text-gray-500">
              {t("announcements.expiresOnDate", { date: formatDate(a.expires_at) })}
            </span>
          )}
        </div>

        <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-gray-800">
          {a.content}
        </p>

        {/* The API returns only the buildings this resident may see -- one
            announcement sent to three buildings shows them theirs. */}
        {a.buildings.length > 0 && (
          <p className="border-t border-gray-100 pt-3 text-[12px] text-gray-500">
            {t("common.building")}: {a.buildings.map((b) => b.name).join(", ")}
          </p>
        )}
      </div>
    </ResponsiveModal>
  );
}
